import type { HttpErrorResponse } from '@angular/common/http';
import { inject, InjectionToken } from '@angular/core';

import type { StoreConfig } from '@ngrx/store';
import { createSelector } from '@ngrx/store';

import type { FeatureCode, FeatureSpec } from '@core/feature-spec-types';
import type { SingleEntityState } from '@core/ngrx-legacy';
import { createSingleEntityStore, memoizeSelector } from '@core/ngrx-legacy';

import type { FeatureSpecEntity, FeatureSpecMap } from './feature-spec.types';
import { FEATURE_SPEC } from './feature-spec.types';

const { actions, selectors, reducer, namespace } = createSingleEntityStore<FeatureSpecEntity>()(
  'FeatureSpec',
  {
    customActions: adapter => ({
      fetchFailed: (state, payload: { error: HttpErrorResponse | Error }) =>
        adapter.setError(state, payload.error.message),
    }),
  },
);

const forCode = <T extends FeatureCode>(code: T) =>
  createSelector(selectors.data, data => {
    const entity = data?.find(feature => feature.codename === code);
    if (!entity) {
      return undefined;
    }
    return <FeatureSpec<T>>entity;
  });

const config = <T extends FeatureCode>(code: T) =>
  createSelector(forCode(code), entity => entity?.configuration);

const FeatureSpecSelectors = {
  ...selectors,
  forCode,
  asMap: createSelector(selectors.data, data =>
    data !== null
      ? data.reduce((acc, feature) => ({ ...acc, [feature.codename]: feature }), <FeatureSpecMap>{})
      : null,
  ),
  config,
  isEnabled: memoizeSelector(<T extends FeatureCode>(code: T) =>
    createSelector(selectors.data, data => {
      const isEnabled = !!data?.find(feature => feature.codename === code)?.enabled;
      return isEnabled;
    }),
  ),
  isEntitled: memoizeSelector(<T extends FeatureCode>(code: T) =>
    createSelector(selectors.data, data => {
      const isEntitled = !!data?.find(feature => feature.codename === code)?.entitled;
      return isEntitled;
    }),
  ),
};

export const FeatureSpecStoreToken = new InjectionToken('FeatureSpecStoreToken', {
  providedIn: 'root',
  factory: (): StoreConfig<SingleEntityState<FeatureSpecEntity>> => ({
    initialState: {
      loaded: true,
      failed: false,
      pending: false,
      error: false,
      data: inject(FEATURE_SPEC),
    },
  }),
});

export {
  FeatureSpecSelectors,
  actions as FeatureSpecActions,
  reducer as FeatureSpecReducer,
  namespace as FeatureSpecNamespace,
};
